import { Injectable } from '@angular/core';

export interface ContactPoint {
  '@type': 'ContactPoint';
  telephone: string;
  contactType?: string;
  contactOption?: string;
  areaServed?: string | string[];
  availableLanguage?: string | string[];
}

export interface PostalAddress {
  '@type': 'PostalAddress';
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  postalCode?: string;
  addressCountry?: string;
}

export interface OrganizationSchema {
  '@context': 'https://schema.org';
  '@type': 'Corporation' | 'Organization';
  name: string;
  alternateName?: string;
  url: string;
  logo?: string;
  telephone?: string;
  email?: string;
  contactPoint?: ContactPoint | ContactPoint[];
  address?: PostalAddress;
  sameAs?: string[];
}

export interface BuildOrganizationInput {
  name: string;
  url: string;
  alternateName?: string;
  logo?: string;
  telephone?: string;
  email?: string;
  contactType?: string;
  contactOption?: string;
  areaServed?: string | string[];
  availableLanguage?: string | string[];
  sameAs?: string[];
  type?: 'Corporation' | 'Organization';
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
}

export type DynamicOrganizationParams = Partial<OrganizationSchema> & {
  url?: string;
};

@Injectable({
  providedIn: 'root',
})
export class SchemaService {
  private organizationSchema?: OrganizationSchema;
  private readonly staticOrganizationSchema: OrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ARIS360 LLC',
    alternateName: 'ARIS360',
    url: 'https://www.aris360.com/',
    logo: 'https://www.aris360.com/assets/img/aris_logo.png',
    telephone: '(206) 240-8514',
    email: 'support@aris360.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '5209 Lake Washington Blvd NE, Suite 220',
      addressLocality: 'Kirkland',
      addressRegion: 'WA',
      postalCode: '98033',
      addressCountry: 'US'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '206-240-8514',
      contactType: 'customer service',
      contactOption: 'HearingImpairedSupported',
      areaServed: 'US',
      availableLanguage: 'en',
    },
    sameAs: [
      'https://www.facebook.com/Aris360llc/',
      'https://twitter.com/aris360llc',
      'https://www.linkedin.com/company/aris360llc/',
      'https://www.instagram.com/aris360llc/',
      'https://www.youtube.com/@Aris-360',
    ],
  };

  buildOrganizationSchema(input: BuildOrganizationInput): OrganizationSchema {
    const schema: OrganizationSchema = {
      '@context': 'https://schema.org',
      '@type': input.type ?? 'Organization',
      name: input.name,
      url: input.url,
    };
    if (input.alternateName) schema.alternateName = input.alternateName;
    if (input.logo) schema.logo = input.logo;
    if (input.telephone) schema.telephone = input.telephone;
    if (input.email) schema.email = input.email;
    if (
      input.telephone ||
      input.contactType ||
      input.contactOption ||
      input.areaServed ||
      input.availableLanguage
    ) {
      const cp: ContactPoint = {
        '@type': 'ContactPoint',
        telephone: input.telephone ?? '',
      };
      if (input.contactType) cp.contactType = input.contactType;
      if (input.contactOption) cp.contactOption = input.contactOption;
      if (input.areaServed) cp.areaServed = input.areaServed;
      if (input.availableLanguage) cp.availableLanguage = input.availableLanguage;
      schema.contactPoint = cp;
    }
    if (input.address) {
      schema.address = {
        '@type': 'PostalAddress',
        streetAddress: input.address.streetAddress,
        addressLocality: input.address.addressLocality,
        addressRegion: input.address.addressRegion,
        postalCode: input.address.postalCode,
        addressCountry: input.address.addressCountry,
      };
    }
    if (input.sameAs && input.sameAs.length) schema.sameAs = input.sameAs;
    this.organizationSchema = schema;
    return schema;
  }

  getStaticOrganizationSchema(): OrganizationSchema {
    return { ...this.staticOrganizationSchema };
  }

  buildMergedOrganizationSchema(dynamic: DynamicOrganizationParams): OrganizationSchema {
    const base = this.getStaticOrganizationSchema();
    const merged = this.mergeOrganizationSchema(base, dynamic);
    this.organizationSchema = merged;
    return merged;
  }

  createJsonLdForStatic(): string {
    return this.toJsonLd(this.getStaticOrganizationSchema());
  }

  createJsonLdForDynamic(dynamic: DynamicOrganizationParams): string {
    const schema = this.buildMergedOrganizationSchema(dynamic);
    return this.toJsonLd(schema);
  }

  setOrganizationSchema(schema: OrganizationSchema): void {
    this.organizationSchema = schema;
  }

  getOrganizationSchema(): OrganizationSchema | undefined {
    return this.organizationSchema;
  }

  validateOrganizationSchema(schema: unknown): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const s: any = schema as any;
    if (!s || typeof s !== 'object') errors.push('Schema must be an object');
    if (!s?.['@context']) errors.push('@context required');
    if (s?.['@context'] && s?.['@context'] !== 'https://schema.org')
      errors.push('@context must be https://schema.org');
    if (!s?.['@type']) errors.push('@type required');
    if (!s?.name) errors.push('name required');
    if (!s?.url) errors.push('url required');
    if (s?.address && s?.address?.['@type'] && s?.address?.['@type'] !== 'PostalAddress')
      errors.push('address must be PostalAddress if provided');
    return { valid: errors.length === 0, errors };
  }

  toJsonLd(schema: object): string {
    return JSON.stringify(schema);
  }

  mergeOrganizationSchema(
    base: OrganizationSchema,
    updates: Partial<OrganizationSchema>
  ): OrganizationSchema {
    return { ...base, ...updates };
  }
}
